// src/components/records/PatientTrendsPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  AppBar,
  Toolbar,
  Fab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ArrowLeft,
  Calendar,
  User,
  Activity,
  Heart,
  Thermometer,
  Droplet,
  Maximize,
  Scale,
  Ruler,
  Download,
  ChevronLeft,
  ChevronRight,
  BarChart2,
  Clock,
  TrendingUp,
  Zap,
  X
} from 'lucide-react';
import { format, parseISO, subMonths } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { HEALTH_GREEN } from '../../Login/utils/mockData';
import agthealthService from '../../../services/agthealthService';

// Importar sidebar y header compartidos
import Sidebar from '../../common/sidebar/Sidebar';
import DashboardHeader from '../../common/header/DashboardHeader';

const DrawerWidth = 240;

const Main = styled(Box)(({ theme, open, isMobile }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  paddingTop: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: isMobile ? 0 : `-${DrawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    paddingTop: 0,
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
  height: '100%',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: HEALTH_GREEN,
  },
  '& .MuiTab-root.Mui-selected': {
    color: HEALTH_GREEN,
    fontWeight: 600,
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    '& .MuiTab-root': {
      minWidth: 'auto',
      padding: theme.spacing(1),
      fontSize: '0.85rem',
    }
  }
}));

const TimeRangeSelector = styled(Paper)(({ theme, isMobile }) => ({
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  marginBottom: theme.spacing(3),
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: isMobile ? 'column' : 'row',
  gap: isMobile ? theme.spacing(1) : 0,
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  fontWeight: 500,
}));

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 2, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {format(new Date(label), "dd/MM/yyyy HH:mm", { locale: es })}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                borderRadius: '50%',
                mr: 1,
              }}
            />
            <Typography variant="body2">
              {entry.name}: {entry.value} {entry.unit}
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  }
  return null;
};

// Componente para mostrar un gráfico individual en dispositivos móviles
const MobileChartCard = ({ title, data, xKey, lines, height = 280, emptyMessage }) => {
  const hasData = data && data.length > 0;

  return (
    <StyledCard sx={{ mb: 2 }}>
      <CardHeader 
        title={title} 
        titleTypographyProps={{ 
          variant: 'subtitle1', 
          fontWeight: 600,
          fontSize: '1rem'
        }}
        action={
          hasData && (
            <Tooltip title="Descargar datos">
              <IconButton size="small">
                <Download size={16} />
              </IconButton>
            </Tooltip>
          )
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ height: height }}>
          {hasData ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 5, right: 5, left: -15, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey={xKey} 
                  tickFormatter={(date) => format(new Date(date), "dd/MM")}
                  angle={-45}
                  textAnchor="end"
                  height={50}
                  tick={{ fontSize: 10 }}
                />
                <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
                {lines.map((line, index) => (
                  <Line 
                    key={index}
                    type="monotone" 
                    dataKey={line.dataKey} 
                    name={line.name} 
                    stroke={line.color} 
                    activeDot={{ r: 6 }}
                    unit={line.unit || ''}
                    strokeWidth={line.strokeWidth || 2}
                    strokeDasharray={line.strokeDasharray || ''}
                    dot={line.dot}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary" variant="body2">
                {emptyMessage || 'No hay datos disponibles'}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </StyledCard>
  );
};

const PatientTrendsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [patient, setPatient] = useState(null);
  const [records, setRecords] = useState([]);
  const [timeRange, setTimeRange] = useState('6months');
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState({
    firstName: 'Dr.',
    lastName: 'Usuario',
    specialty: 'Profesional de la salud'
  });
  
  // Estado para el drawer de selección de gráficos en móvil
  const [chartSelectorOpen, setChartSelectorOpen] = useState(false);
  
  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Obtener el perfil del usuario
        const userResponse = await agthealthService.getUserProfile();
        if (userResponse.data) {
          setUser(userResponse.data);
        }
        
        // Obtener datos del paciente
        if (patientId) {
          const patientResponse = await agthealthService.getPatientById(patientId);
          if (patientResponse.data) {
            setPatient(patientResponse.data);
          }
          
          // Obtener registros del paciente
          await fetchPatientRecords(patientId);
        }
        
        setError(null);
      } catch (err) {
        console.error("Error al cargar datos:", err);
        setError("No se pudieron cargar los datos del paciente");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [patientId]);
  
  // Función para obtener registros con filtro de tiempo
  const fetchPatientRecords = async (id) => {
    try {
      setLoading(true);
      
      let startDate = '';
      const endDate = new Date().toISOString();
      
      // Calcular fecha de inicio según el rango seleccionado
      switch (timeRange) {
        case '1month':
          startDate = subMonths(new Date(), 1).toISOString();
          break;
        case '3months':
          startDate = subMonths(new Date(), 3).toISOString();
          break;
        case '6months':
          startDate = subMonths(new Date(), 6).toISOString();
          break;
        case '1year':
          startDate = subMonths(new Date(), 12).toISOString();
          break;
        case 'all':
          startDate = new Date(0).toISOString(); // Desde el inicio de los tiempos
          break;
        default:
          startDate = subMonths(new Date(), 6).toISOString();
      }
      
      // Obtener registros del paciente en el rango de fechas
      const response = await agthealthService.getRecordsByPatient(id);
      
      if (response.data) {
        // Filtrar por rango de fechas
        const filteredRecords = response.data.filter(record => 
          record.date >= startDate && record.date <= endDate
        );
        
        // Ordenar registros por fecha (más antiguos primero)
        const sortedRecords = filteredRecords.sort((a, b) => 
          new Date(a.date) - new Date(b.date)
        );
        
        setRecords(sortedRecords);
      }
      
      setError(null);
    } catch (err) {
      console.error("Error al obtener registros del paciente:", err);
      setError("No se pudieron cargar los registros del paciente");
    } finally {
      setLoading(false);
    }
  };
  
  // Preparar datos para gráficos
  const prepareChartData = (field, path = null) => {
    return records.map(record => {
      let value = path 
        ? path.split('.').reduce((obj, key) => obj && obj[key], record)
        : record[field];
      
      return {
        date: record.date,
        [field]: value !== undefined && value !== null ? Number(value) : null
      };
    }).filter(item => item[field] !== null);
  };
  
  // Manejar cambio de pestaña
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Manejar cambio de rango de tiempo
  const handleTimeRangeChange = (event) => {
    setTimeRange(event.target.value);
    fetchPatientRecords(patientId);
  };
  
  // Manejadores de eventos
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = async () => {
    try {
      await agthealthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };
  
  const handleSettings = () => {
    navigate('/settings');
  };
  
  const handleBack = () => {
    navigate('/records');
  };
  
  const handleOpenChartSelector = () => {
    setChartSelectorOpen(true);
  };

  const handleCloseChartSelector = () => {
    setChartSelectorOpen(false);
  };
  
  // Renderizar pestaña de signos vitales
  const renderVitalSignsTab = () => {
    // Versión para dispositivos móviles
    if (isMobile) {
      return (
        <Box>
          {/* Presión arterial */}
          <MobileChartCard
            title="Presión Arterial"
            data={records.map(record => ({
              date: record.date,
              systolicBP: record.vitalSigns?.systolicBP,
              diastolicBP: record.vitalSigns?.diastolicBP
            }))}
            xKey="date"
            lines={[
              { dataKey: "systolicBP", name: "Sistólica", color: "#f44336", unit: "mmHg" },
              { dataKey: "diastolicBP", name: "Diastólica", color: "#2196f3", unit: "mmHg" }
            ]}
            emptyMessage="No hay datos de presión arterial disponibles"
          />
          
          {/* Frecuencia cardíaca */}
          <MobileChartCard
            title="Frecuencia Cardíaca"
            data={records.map(record => ({
              date: record.date,
              heartRate: record.vitalSigns?.heartRate
            }))}
            xKey="date"
            lines={[
              { dataKey: "heartRate", name: "Frecuencia cardíaca", color: "#ff9800", unit: "lpm" }
            ]}
            emptyMessage="No hay datos de frecuencia cardíaca disponibles"
          />
          
          {/* Saturación de oxígeno */}
          <MobileChartCard
            title="Saturación de Oxígeno"
            data={records.map(record => ({
              date: record.date,
              oxygenSaturation: record.vitalSigns?.oxygenSaturation
            }))}
            xKey="date"
            lines={[
              { dataKey: "oxygenSaturation", name: "Saturación de oxígeno", color: "#4caf50", unit: "%" }
            ]}
            emptyMessage="No hay datos de saturación de oxígeno disponibles"
          />
          
          {/* Temperatura */}
          <MobileChartCard
            title="Temperatura"
            data={records.map(record => ({
              date: record.date,
              temperature: record.vitalSigns?.temperature
            }))}
            xKey="date"
            lines={[
              { dataKey: "temperature", name: "Temperatura", color: "#e91e63", unit: "°C" }
            ]}
            emptyMessage="No hay datos de temperatura disponibles"
          />
        </Box>
      );
    }
    
    // Versión para escritorio
    return (
      <Grid container spacing={3}>
        {/* Presión arterial */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Presión Arterial" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('systolicBP', 'vitalSigns.systolicBP').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        systolicBP: record.vitalSigns?.systolicBP,
                        diastolicBP: record.vitalSigns?.diastolicBP
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 10', 'dataMax + 10']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="systolicBP" 
                        name="Sistólica" 
                        stroke="#f44336" 
                        activeDot={{ r: 6 }}
                        unit="mmHg"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="diastolicBP" 
                        name="Diastólica" 
                        stroke="#2196f3" 
                        activeDot={{ r: 6 }}
                        unit="mmHg"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de presión arterial disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        
        {/* Frecuencia cardíaca */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Frecuencia Cardíaca" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('heartRate', 'vitalSigns.heartRate').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        heartRate: record.vitalSigns?.heartRate
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 10', 'dataMax + 10']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="heartRate" 
                        name="Frecuencia cardíaca" 
                        stroke="#ff9800" 
                        activeDot={{ r: 6 }}
                        unit="lpm"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de frecuencia cardíaca disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        
        {/* Saturación de oxígeno */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Saturación de Oxígeno" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('oxygenSaturation', 'vitalSigns.oxygenSaturation').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        oxygenSaturation: record.vitalSigns?.oxygenSaturation
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={[85, 100]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="oxygenSaturation" 
                        name="Saturación de oxígeno" 
                        stroke="#4caf50" 
                        activeDot={{ r: 6 }}
                        unit="%"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de saturación de oxígeno disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        
        {/* Temperatura */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Temperatura" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('temperature', 'vitalSigns.temperature').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        temperature: record.vitalSigns?.temperature
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={[35, 40]} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="temperature" 
                        name="Temperatura" 
                        stroke="#e91e63" 
                        activeDot={{ r: 6 }}
                        unit="°C"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de temperatura disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    );
  };
  
  // Renderizar pestaña de métricas corporales
  const renderBodyMetricsTab = () => {
    // Versión para dispositivos móviles
    if (isMobile) {
      return (
        <Box>
          {/* Peso */}
          <MobileChartCard
            title="Peso"
            data={records.map(record => ({
              date: record.date,
              weight: record.vitalSigns?.weight
            }))}
            xKey="date"
            lines={[
              { dataKey: "weight", name: "Peso", color: "#673ab7", unit: "kg" }
            ]}
            emptyMessage="No hay datos de peso disponibles"
          />
          
          {/* Altura */}
          <MobileChartCard
            title="Altura"
            data={records.map(record => ({
              date: record.date,
              height: record.vitalSigns?.height
            }))}
            xKey="date"
            lines={[
              { dataKey: "height", name: "Altura", color: "#3f51b5", unit: "cm" }
            ]}
            emptyMessage="No hay datos de altura disponibles"
          />
          
          {/* IMC calculado */}
          <MobileChartCard
            title="Índice de Masa Corporal (IMC)"
            data={records.map(record => {
              const weight = record.vitalSigns?.weight;
              const height = record.vitalSigns?.height;
              let bmi = null;
              
              if (weight && height) {
                const heightInMeters = height / 100;
                bmi = Number((weight / (heightInMeters * heightInMeters)).toFixed(2));
              }
              
              return {
                date: record.date,
                bmi
              };
            }).filter(item => item.bmi !== null)}
            xKey="date"
            lines={[
              { dataKey: "bmi", name: "IMC", color: "#009688", unit: "kg/m²" },
              { dataKey: () => 18.5, name: "Bajo peso/Normal", color: "#ffeb3b", strokeDasharray: "3 3", dot: false },
              { dataKey: () => 25, name: "Normal/Sobrepeso", color: "#ff9800", strokeDasharray: "3 3", dot: false },
              { dataKey: () => 30, name: "Sobrepeso/Obesidad", color: "#f44336", strokeDasharray: "3 3", dot: false }
            ]}
            emptyMessage="No hay datos suficientes para calcular el IMC"
          />
        </Box>
      );
    }
    
    // Versión para escritorio
    return (
      <Grid container spacing={3}>
        {/* Peso */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Peso" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('weight', 'vitalSigns.weight').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        weight: record.vitalSigns?.weight
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 5', 'dataMax + 5']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="weight" 
                        name="Peso" 
                        stroke="#673ab7" 
                        activeDot={{ r: 6 }}
                        unit="kg"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de peso disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        
        {/* Altura */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Altura" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('height', 'vitalSigns.height').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        height: record.vitalSigns?.height
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 5', 'dataMax + 5']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="height" 
                        name="Altura" 
                        stroke="#3f51b5" 
                        activeDot={{ r: 6 }}
                        unit="cm"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de altura disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
        
        {/* IMC calculado */}
        <Grid item xs={12}>
          <StyledCard>
            <CardHeader 
              title="Índice de Masa Corporal (IMC)" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {records.length > 0 && records.some(r => r.vitalSigns?.weight && r.vitalSigns?.height) ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => {
                        const weight = record.vitalSigns?.weight;
                        const height = record.vitalSigns?.height;
                        let bmi = null;
                        
                        if (weight && height) {
                          const heightInMeters = height / 100;
                          bmi = Number((weight / (heightInMeters * heightInMeters)).toFixed(2));
                        }
                        
                        return {
                          date: record.date,
                          bmi
                        };
                      }).filter(item => item.bmi !== null)}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 2', 'dataMax + 2']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="bmi" 
                        name="IMC" 
                        stroke="#009688" 
                        activeDot={{ r: 6 }}
                        unit="kg/m²"
                      />
                      {/* Líneas de referencia para categorías de IMC */}
                      <RechartsTooltip />
                      <Line 
                        dataKey={() => 18.5} 
                        stroke="#ffeb3b" 
                        strokeDasharray="3 3" 
                        name="Bajo peso/Normal" 
                        dot={false}
                      />
                      <Line 
                        dataKey={() => 25} 
                        stroke="#ff9800" 
                        strokeDasharray="3 3" 
                        name="Normal/Sobrepeso" 
                        dot={false}
                      />
                      <Line 
                        dataKey={() => 30} 
                        stroke="#f44336" 
                        strokeDasharray="3 3" 
                        name="Sobrepeso/Obesidad" 
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos suficientes para calcular el IMC
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    );
  };
  
  // Renderizar pestaña de laboratorio
  const renderLabValuesTab = () => {
    // Versión para dispositivos móviles
    if (isMobile) {
      return (
        <Box>
          {/* Glucosa en sangre */}
          <MobileChartCard
            title="Glucosa en Sangre"
            data={records.map(record => ({
              date: record.date,
              bloodGlucose: record.vitalSigns?.bloodGlucose
            }))}
            xKey="date"
            lines={[
              { dataKey: "bloodGlucose", name: "Glucosa", color: "#8bc34a", unit: "mg/dL" },
              { dataKey: () => 70, name: "Mínimo normal", color: "#ffeb3b", strokeDasharray: "3 3", dot: false },
              { dataKey: () => 100, name: "Máximo normal", color: "#ff9800", strokeDasharray: "3 3", dot: false }
            ]}
            emptyMessage="No hay datos de glucosa en sangre disponibles"
          />
        </Box>
      );
    }
    
    // Versión para escritorio
    return (
      <Grid container spacing={3}>
        {/* Glucosa en sangre */}
        <Grid item xs={12} md={6}>
          <StyledCard>
            <CardHeader 
              title="Glucosa en Sangre" 
              titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
              action={
                <Tooltip title="Descargar datos">
                  <IconButton>
                    <Download size={18} />
                  </IconButton>
                </Tooltip>
              }
            />
            <CardContent>
              <Box sx={{ height: 300 }}>
                {prepareChartData('bloodGlucose', 'vitalSigns.bloodGlucose').length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={records.map(record => ({
                        date: record.date,
                        bloodGlucose: record.vitalSigns?.bloodGlucose
                      }))}
                      margin={{ top: 5, right: 5, left: 5, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => format(new Date(date), "dd/MM")}
                        angle={-45}
                        textAnchor="end"
                        height={50}
                      />
                      <YAxis domain={['dataMin - 10', 'dataMax + 10']} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="bloodGlucose" 
                        name="Glucosa" 
                        stroke="#8bc34a" 
                        activeDot={{ r: 6 }}
                        unit="mg/dL"
                      />
                      {/* Líneas de referencia para valores normales */}
                      <Line 
                        dataKey={() => 70} 
                        stroke="#ffeb3b" 
                        strokeDasharray="3 3" 
                        name="Mínimo normal" 
                        dot={false}
                      />
                      <Line 
                        dataKey={() => 100} 
                        stroke="#ff9800" 
                        strokeDasharray="3 3" 
                        name="Máximo normal" 
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary">
                      No hay datos de glucosa en sangre disponibles
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>
    );
  };
  
  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar compartido */}
      <Sidebar
        open={drawerOpen}
        isMobile={isMobile}
        onClose={handleDrawerToggle}
        onLogout={handleLogout}
        activeSection="records"
      />
      
      {/* Main Content */}
      <Main open={drawerOpen} isMobile={isMobile}>
        <DashboardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={handleBack}
                sx={{ mr: 1 }}
                aria-label="volver"
                size={isSmall ? "small" : "medium"}
              >
                <ArrowLeft size={isSmall ? 18 : 24} />
              </IconButton>
              {isSmall ? "Tendencias" : "Tendencias del Paciente"}
            </Box>
          }
          user={user}
          isMobile={isMobile}
          onMenuClick={handleDrawerToggle}
          onLogout={handleLogout}
          onSettings={handleSettings}
        />
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: HEALTH_GREEN }} />
          </Box>
        ) : (
          <>
            {/* Información del paciente - Versión adaptativa */}
            {patient && (
              <Paper sx={{ 
                p: isMobile ? 2 : 3, 
                mb: 3, 
                borderRadius: 3 
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: isMobile ? 1 : 2,
                  flexDirection: isMobile ? 'column' : 'row',
                  textAlign: isMobile ? 'center' : 'left'
                }}>
                  <User 
                    size={isMobile ? 32 : 24} 
                    style={{ 
                      marginRight: isMobile ? 0 : 12, 
                      marginBottom: isMobile ? 8 : 0,
                      color: HEALTH_GREEN 
                    }} 
                  />
                  <Typography 
                    variant={isMobile ? "h6" : "h5"} 
                    sx={{ fontWeight: 700 }}
                  >
                    {patient.firstName} {patient.lastName}
                  </Typography>
                </Box>
                
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmall ? '0.7rem' : undefined }}>
                      Identificación
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: isSmall ? '0.85rem' : undefined }}>
                      {patient.identificationNumber}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmall ? '0.7rem' : undefined }}>
                      Edad
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: isSmall ? '0.85rem' : undefined }}>
                      {patient.age} años
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmall ? '0.7rem' : undefined }}>
                      Género
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: isSmall ? '0.85rem' : undefined }}>
                      {patient.gender === 'male' ? 'Masculino' : 
                       patient.gender === 'female' ? 'Femenino' : 
                       patient.gender === 'other' ? 'Otro' : 'No especificado'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmall ? '0.7rem' : undefined }}>
                      Total registros
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: isSmall ? '0.85rem' : undefined }}>
                      {records.length}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
            
            {/* Selector de rango de tiempo - Adaptativo */}
            <TimeRangeSelector elevation={0} isMobile={isMobile}>
              <Typography variant={isSmall ? "body1" : "subtitle1"} sx={{ fontWeight: 600 }}>
                Período de tiempo
              </Typography>
              
              <FormControl sx={{ width: isMobile ? '100%' : 200 }}>
                <Select
                  value={timeRange}
                  onChange={handleTimeRangeChange}
                  displayEmpty
                  variant="outlined"
                  size={isSmall ? "small" : "medium"}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="1month">Último mes</MenuItem>
                  <MenuItem value="3months">Últimos 3 meses</MenuItem>
                  <MenuItem value="6months">Últimos 6 meses</MenuItem>
                  <MenuItem value="1year">Último año</MenuItem>
                  <MenuItem value="all">Todo el historial</MenuItem>
                </Select>
              </FormControl>
            </TimeRangeSelector>
            
            {/* Pestañas para diferentes tipos de gráficos - Adaptativo */}
            <StyledTabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant={isMobile ? "fullWidth" : "standard"}
            >
              <Tab 
                label={isSmall ? "Vitales" : "Signos Vitales"} 
                icon={isSmall ? <Activity size={16} /> : <Activity size={18} />} 
                iconPosition="start"
              />
              <Tab 
                label={isSmall ? "Métricas" : "Métricas Corporales"} 
                icon={isSmall ? <Scale size={16} /> : <Scale size={18} />} 
                iconPosition="start"
              />
              <Tab 
                label={isSmall ? "Lab" : "Valores de Laboratorio"} 
                icon={isSmall ? <Droplet size={16} /> : <Droplet size={18} />} 
                iconPosition="start"
              />
            </StyledTabs>
            
            {/* Contenido según la pestaña seleccionada */}
            {records.length === 0 ? (
              <Alert 
                severity="info" 
                sx={{ 
                  borderRadius: 2,
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  color: HEALTH_GREEN
                }}
              >
                No hay registros disponibles para este paciente en el período seleccionado.
              </Alert>
            ) : (
              <>
                {tabValue === 0 && renderVitalSignsTab()}
                {tabValue === 1 && renderBodyMetricsTab()}
                {tabValue === 2 && renderLabValuesTab()}
              </>
            )}
            
            {/* Botón flotante para selección de gráficos en móvil */}
            {isMobile && records.length > 0 && (
              <Fab
                color="primary"
                aria-label="seleccionar gráfico"
                sx={{
                  position: 'fixed',
                  bottom: 20,
                  right: 20,
                  backgroundColor: HEALTH_GREEN,
                  '&:hover': {
                    backgroundColor: '#3d8b40',
                  },
                }}
                onClick={handleOpenChartSelector}
              >
                <BarChart2 size={24} />
              </Fab>
            )}
            
            {/* Drawer para selección de gráficos en móvil */}
            {isMobile && (
              <SwipeableDrawer
                anchor="bottom"
                open={chartSelectorOpen}
                onClose={handleCloseChartSelector}
                onOpen={() => {}}
                PaperProps={{
                  sx: {
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                    maxWidth: '100%',
                    width: '100%',
                  },
                }}
              >
                <Box sx={{ py: 1 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      textAlign: 'center', 
                      fontWeight: 600,
                      py: 1,
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      position: 'relative'
                    }}
                  >
                    Seleccionar Gráfico
                    <IconButton
                      onClick={handleCloseChartSelector}
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    >
                      <X size={18} />
                    </IconButton>
                  </Typography>
                  
                  <List sx={{ pt: 0 }}>
                    <ListItem button onClick={() => { setTabValue(0); handleCloseChartSelector(); }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Activity size={20} color={tabValue === 0 ? HEALTH_GREEN : undefined} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Signos Vitales" 
                        primaryTypographyProps={{ 
                          sx: { color: tabValue === 0 ? HEALTH_GREEN : undefined, fontWeight: tabValue === 0 ? 600 : 400 } 
                        }}
                      />
                      {tabValue === 0 && <Chip label="Actual" size="small" sx={{ backgroundColor: HEALTH_GREEN, color: 'white' }} />}
                    </ListItem>
                    
                    <ListItem button onClick={() => { setTabValue(1); handleCloseChartSelector(); }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Scale size={20} color={tabValue === 1 ? HEALTH_GREEN : undefined} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Métricas Corporales" 
                        primaryTypographyProps={{ 
                          sx: { color: tabValue === 1 ? HEALTH_GREEN : undefined, fontWeight: tabValue === 1 ? 600 : 400 } 
                        }}
                      />
                      {tabValue === 1 && <Chip label="Actual" size="small" sx={{ backgroundColor: HEALTH_GREEN, color: 'white' }} />}
                    </ListItem>
                    
                    <ListItem button onClick={() => { setTabValue(2); handleCloseChartSelector(); }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Droplet size={20} color={tabValue === 2 ? HEALTH_GREEN : undefined} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Valores de Laboratorio" 
                        primaryTypographyProps={{ 
                          sx: { color: tabValue === 2 ? HEALTH_GREEN : undefined, fontWeight: tabValue === 2 ? 600 : 400 } 
                        }}
                      />
                      {tabValue === 2 && <Chip label="Actual" size="small" sx={{ backgroundColor: HEALTH_GREEN, color: 'white' }} />}
                    </ListItem>
                  </List>
                </Box>
              </SwipeableDrawer>
            )}
          </>
        )}
      </Main>
    </Box>
  );
};

export default PatientTrendsPage;